import React, { useState, useContext, useEffect } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import moment from "moment";
import axios from "axios";
import * as XLSX from 'xlsx';

import { AuthContext } from "../contexts/AuthContext";
import { AdminContext } from "../contexts/AdminContext";
import { ClientContext } from "../contexts/ClientContext";
import { ScreenContext } from "../contexts/ScreenContext";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormLabel from "@material-ui/core/FormLabel";
import Switch from "@material-ui/core/Switch";
import Divider from "@material-ui/core/Divider";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import LinearProgress from "@material-ui/core/LinearProgress";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import GetAppIcon from "@material-ui/icons/GetApp";
import Autocomplete from "@material-ui/lab/Autocomplete";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  searchForm: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
    },
  },
  pageTitle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  formSection: {
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  inputField: {
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    flexWrap: 'wrap',
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(2),
      padding: theme.spacing(1.5),
    },
  },
  switchGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(0.5),
      width: '100%',
      margin: 0,
    },
  },
  switchLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    minWidth: '80px',
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
      fontSize: '0.8rem',
      fontWeight: 500,
      textAlign: 'left',
      marginBottom: theme.spacing(0.5),
    },
  },
  toggleButtonGroup: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      '& .MuiToggleButton-root': {
        flex: 1,
        padding: '4px 4px !important',
        fontSize: '0.65rem !important',
        minWidth: '0 !important',
        whiteSpace: 'nowrap',
      },
    },
  },
  divider: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  mobileButtonGroup: {
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
    '& button': {
      flex: 1,
    },
  },
  searchButton: {
    [theme.breakpoints.down('sm')]: {
      flex: 1,
    },
  },
  clearButton: {
    [theme.breakpoints.down('sm')]: {
      flex: 1,
    },
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(1),
    marginLeft: 'auto',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginLeft: 0,
      flexDirection: 'column',
      '& .MuiButton-root': {
        width: '100%',
      },
    },
  },
  resultsContainer: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
    },
  },
  exportButton: {
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginTop: theme.spacing(1),
      width: '100%',
    },
  },
  tableContainer: {
    [theme.breakpoints.down('sm')]: {
      '& .MuiTableCell-root': {
        padding: theme.spacing(1),
        fontSize: '0.75rem',
      },
      '& .MuiTableCell-head': {
        fontWeight: 600,
        position: 'sticky',
        top: 0,
        backgroundColor: theme.palette.background.paper,
        zIndex: 1,
      },
    },
  },
  table: {
    '& .MuiTableCell-head': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[100],
      borderBottom: `2px solid ${theme.palette.divider}`,
      whiteSpace: 'nowrap',
      padding: theme.spacing(1.5, 2),
    },
    '& .MuiTableBody-root .MuiTableRow-root': {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '& .MuiTableCell-root': {
        padding: theme.spacing(1.5, 2),
      },
    },
    '& .dateColumn': {
      width: '100px',
      whiteSpace: 'nowrap',
    },
    '& .userColumn': {
      width: '70px',
      textAlign: 'center',
      fontWeight: 500,
    },
    '& .clientColumn': {
      width: '180px',
    },
    '& .hoursColumn': {
      width: '70px',
      textAlign: 'center',
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
    '& .descriptionColumn': {
      minWidth: '250px',
      maxWidth: '400px',
      '& .descriptionContent': {
        position: 'relative',
      },
      '& .descriptionText': {
        '&.collapsed': {
          maxHeight: '60px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
        },
        '&.expanded': {
          maxHeight: 'none',
        },
      },
      '& .readMoreLink': {
        color: theme.palette.primary.main,
        cursor: 'pointer',
        fontSize: '0.875rem',
        fontWeight: 500,
        marginTop: theme.spacing(0.5),
        display: 'inline-block',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    '& .platformColumn': {
      width: '140px',
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
    },
    '& .categoryColumn': {
      width: '100px',
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
    },
  },
  summaryChips: {
    margin: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
      height: '24px',
    },
  },
  summaryContainer: {
    [theme.breakpoints.down('sm')]: {
      '& .MuiTypography-h6': {
        fontSize: '1rem',
      },
    },
  },
  noResults: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
}));

const SearchHours = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, token } = useContext(AuthContext);
  const { users, loadUsers } = useContext(AdminContext);
  const { clients, loadClients } = useContext(ClientContext);
  const { setSnackbarMessage } = useContext(ScreenContext);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/";

  // Search criteria state
  const [searchCriteria, setSearchCriteria] = useState({
    user: user.username,
    dateFrom: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    dateTo: moment().format('YYYY-MM-DD'),
    keywords: '',
    clientId: '',
    category: '',
    platform: '',
    includeRnD: null,
    includeDirect: null  // null = All, true = Direct only, false = Indirect only
  });

  // Results state
  const [searchResults, setSearchResults] = useState([]);
  const [searchSummary, setSearchSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Track which descriptions are expanded
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Selected client object for autocomplete
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Helper function to strip HTML tags and check if text is long enough to truncate
  const isTextLongEnoughForReadMore = (htmlText) => {
    if (!htmlText) return false;
    // Strip HTML tags to get plain text length
    const plainText = htmlText.replace(/<[^>]*>/g, '');
    // Use a higher threshold - if plain text is > 200 chars, likely to overflow 3 lines
    return plainText.length > 200;
  };

  // Load users and clients on mount
  useEffect(() => {
    loadUsers();
    loadClients();
  }, []);

  const handleInputChange = (field, value) => {
    setSearchCriteria(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const performSearch = async () => {
    setIsLoading(true);
    setHasSearched(true);

    try {
      // Prepare search payload - be explicit about all fields
      const searchPayload = {
        user: searchCriteria.user || null,
        dateFrom: searchCriteria.dateFrom || null,
        dateTo: searchCriteria.dateTo || null,
        keywords: searchCriteria.keywords || null,
        clientId: searchCriteria.clientId || null,
        category: searchCriteria.category || null,
        platform: searchCriteria.platform || null,
        includeRnD: searchCriteria.includeRnD,
        includeDirect: searchCriteria.includeDirect
      };

      const response = await axios.post(
        API_URL + "search-hours",
        searchPayload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSearchResults(response.data.hours);
      
      // Calculate summary based on user permissions
      let summaryToDisplay;
      if (user.type === 'super_admin') {
        // Super admin sees all hours
        summaryToDisplay = response.data.summary;
      } else {
        // Regular users only see their own hours in summary
        const userEntries = response.data.hours.filter(h => h.Who === user.username);
        const userHours = userEntries.reduce((sum, hour) => sum + parseFloat(hour.Qty || 0), 0);
        summaryToDisplay = {
          totalEntries: response.data.summary.totalEntries, // Still show total entries found
          totalHours: parseFloat(userHours.toFixed(2)), // But only their hours
          uniqueUsers: response.data.summary.uniqueUsers,
          uniqueClients: response.data.summary.uniqueClients
        };
      }
      
      setSearchSummary(summaryToDisplay);
      
      // Reset expanded rows when new search is performed
      setExpandedRows(new Set());
      
      const hoursMessage = user.type === 'super_admin' 
        ? `Found ${summaryToDisplay.totalEntries} entries with ${summaryToDisplay.totalHours} total hours`
        : `Found ${summaryToDisplay.totalEntries} entries (${summaryToDisplay.totalHours} hours are yours)`;
      setSnackbarMessage(hoursMessage);
    } catch (error) {
      console.error("Search error:", error);
      setSnackbarMessage("Search failed: " + (error.response?.data || error.message));
      setSearchResults([]);
      setSearchSummary(null);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRowExpansion = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const clearSearch = () => {
    setSearchCriteria({
      user: user.username,
      dateFrom: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      dateTo: moment().format('YYYY-MM-DD'),
      keywords: '',
      clientId: '',
      category: '',
      platform: '',
      includeRnD: null,
      includeDirect: null
    });
    setSelectedClient(null);
    setSearchResults([]);
    setSearchSummary(null);
    setHasSearched(false);
  };

  const exportToExcel = () => {
    if (!searchResults || searchResults.length === 0) {
      setSnackbarMessage("No data to export");
      return;
    }

    // Export all search results - honors the search criteria exactly
    const dataToExport = searchResults;

    // Prepare data for Excel
    const excelData = dataToExport.map(hour => ({
      Date: moment(hour.Completed).format('MM/DD/YYYY'),
      User: hour.Who,
      Client: hour.ClientName || `ID: ${hour.ID}`,
      Hours: hour.Qty,
      Description: hour.Description ? hour.Description.replace(/<[^>]*>/g, '') : '', // Strip HTML
      Platform: hour.Platform || '',
      Category: hour.Category || '',
      'Work Type': hour.DorI || '',
      'R&D': hour.isRND ? 'Yes' : 'No'
    }));

    // Create worksheet and workbook
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Hours");

    // Auto-size columns
    const maxWidth = excelData.reduce((w, r) => Math.max(w, r.Description?.length || 0), 10);
    worksheet['!cols'] = [
      { wch: 12 }, // Date
      { wch: 10 }, // User
      { wch: 25 }, // Client
      { wch: 8 },  // Hours
      { wch: Math.min(maxWidth, 50) }, // Description
      { wch: 15 }, // Platform
      { wch: 12 }, // Category
      { wch: 12 }, // Work Type
      { wch: 8 }   // R&D
    ];

    // Generate filename with date range
    const filename = `Hours_${moment(searchCriteria.dateFrom).format('MMDDYYYY')}_to_${moment(searchCriteria.dateTo).format('MMDDYYYY')}.xlsx`;
    
    // Download
    XLSX.writeFile(workbook, filename);
    setSnackbarMessage(`Exported ${dataToExport.length} entries to ${filename}`);
  };

  return (
    <div className={classes.root}>
      <Typography variant="h4" gutterBottom className={classes.pageTitle}>
        Search Hours
      </Typography>
      
      {/* Search Form */}
      <Paper className={classes.searchForm} elevation={1}>
        <Typography variant="h6" gutterBottom style={{ marginBottom: isMobile ? 8 : 16, fontSize: isMobile ? '1rem' : '1.25rem' }}>
          Search Criteria
        </Typography>
        
        <Grid container spacing={isMobile ? 1.5 : 2}>
          {/* User Selection */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel id="search-user-label">User</InputLabel>
              <Select
                labelId="search-user-label"
                id="search-user"
                name="user"
                label="User"
                value={searchCriteria.user}
                onChange={(e) => handleInputChange('user', e.target.value)}
              >
                <MenuItem value="all">All Users</MenuItem>
                {users.length === 0 && (
                  <MenuItem disabled>
                    <em>Loading users...</em>
                  </MenuItem>
                )}
                {users.map(u => (
                  <MenuItem key={u.username} value={u.username}>
                    {u.first_name} {u.last_name} ({u.username})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Date From */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              id="search-date-from"
              name="dateFrom"
              label="Date From"
              type="date"
              value={searchCriteria.dateFrom}
              onChange={(e) => handleInputChange('dateFrom', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>

          {/* Date To */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              id="search-date-to"
              name="dateTo"
              label="Date To"
              type="date"
              value={searchCriteria.dateTo}
              onChange={(e) => handleInputChange('dateTo', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>

          {/* Client Filter */}
          <Grid item xs={12} sm={6} md={5}>
            <Autocomplete
              options={clients}
              getOptionLabel={(option) => `${option.ID} - ${option.Name}`}
              value={selectedClient}
              onChange={(event, newValue) => {
                setSelectedClient(newValue);
                handleInputChange('clientId', newValue ? newValue.ID : '');
              }}
              size="small"
              renderInput={(params) => (
                <TextField 
                  {...params}
                  id="search-client"
                  name="client"
                  label="Client" 
                  placeholder="Start typing client name or ID..."
                  fullWidth
                />
              )}
              filterOptions={(options, { inputValue }) => {
                return options.filter(option => 
                  option.Name.toLowerCase().includes(inputValue.toLowerCase()) ||
                  option.ID.toString().includes(inputValue)
                );
              }}
            />
          </Grid>

          {/* Keywords */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              id="search-keywords"
              name="keywords"
              label="Keywords"
              placeholder="e.g., meeting, bug fix"
              value={searchCriteria.keywords}
              onChange={(e) => handleInputChange('keywords', e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>

          {/* Platform Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              id="search-platform"
              name="platform"
              label="Platform"
              placeholder="e.g., Web, Mobile, API"
              value={searchCriteria.platform}
              onChange={(e) => handleInputChange('platform', e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>

          {/* Work Type, Category, and R&D Filters */}
          <Grid item xs={12}>
            <Box className={classes.filterRow}>
              {/* Work Type Toggle - Direct/Indirect */}
              <div className={classes.switchGroup}>
                <Typography className={classes.switchLabel}>Work Type:</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  className={classes.toggleButtonGroup}
                  value={searchCriteria.includeDirect === null ? 'all' : searchCriteria.includeDirect ? 'direct' : 'indirect'}
                  onChange={(e, newValue) => {
                    if (newValue === null) return; // Don't allow deselect
                    handleInputChange('includeDirect', newValue === 'all' ? null : newValue === 'direct' ? true : false);
                  }}
                >
                  <ToggleButton value="all">
                    All
                  </ToggleButton>
                  <ToggleButton value="direct">
                    Direct
                  </ToggleButton>
                  <ToggleButton value="indirect">
                    Indirect
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>

              <Divider orientation="vertical" flexItem className={classes.divider} />

              {/* Category Toggle */}
              <div className={classes.switchGroup}>
                <Typography className={classes.switchLabel}>Category:</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  className={classes.toggleButtonGroup}
                  value={searchCriteria.category || 'all'}
                  onChange={(e, newValue) => {
                    if (newValue === null) return; // Don't allow deselect
                    handleInputChange('category', newValue === 'all' ? '' : newValue);
                  }}
                >
                  <ToggleButton value="all">
                    All
                  </ToggleButton>
                  <ToggleButton value="Service">
                    Service
                  </ToggleButton>
                  <ToggleButton value="Admin">
                    Admin
                  </ToggleButton>
                  <ToggleButton value="NonProd">
                    NonProd
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>

              <Divider orientation="vertical" flexItem className={classes.divider} />

              {/* R&D Toggle */}
              <div className={classes.switchGroup}>
                <Typography className={classes.switchLabel}>R&D Work:</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  className={classes.toggleButtonGroup}
                  value={searchCriteria.includeRnD === null ? 'all' : searchCriteria.includeRnD ? 'only' : 'exclude'}
                  onChange={(e, newValue) => {
                    if (newValue === null) return; // Don't allow deselect
                    handleInputChange('includeRnD', newValue === 'all' ? null : newValue === 'only' ? true : false);
                  }}
                >
                  <ToggleButton value="all">
                    All
                  </ToggleButton>
                  <ToggleButton value="only">
                    Only
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>

              {/* Action Buttons */}
              <Box className={classes.buttonGroup}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={performSearch}
                  disabled={isLoading}
                  className={classes.searchButton}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearSearch}
                  disabled={isLoading}
                  className={classes.clearButton}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading */}
      {isLoading && <LinearProgress />}

      {/* Search Summary */}
      {searchSummary && (
        <Box className={classes.resultsContainer}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
            <Typography variant="h6">
              Search Results Summary
            </Typography>
            {user.type === 'super_admin' && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<GetAppIcon />}
                onClick={exportToExcel}
                className={classes.exportButton}
                size={isMobile ? "small" : "medium"}
              >
                Export to Excel
              </Button>
            )}
          </Box>
          <div>
            <Chip 
              label={`${searchSummary.totalEntries} entries`}
              className={classes.summaryChips}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
            <Chip 
              label={user.type === 'super_admin' ? `${searchSummary.totalHours} hours` : `${searchSummary.totalHours} hours (yours)`}
              className={classes.summaryChips}
              color="secondary"
              size={isMobile ? "small" : "medium"}
            />
            <Chip 
              label={`${searchSummary.uniqueUsers} users`}
              className={classes.summaryChips}
              size={isMobile ? "small" : "medium"}
            />
            <Chip 
              label={`${searchSummary.uniqueClients} clients`}
              className={classes.summaryChips}
              size={isMobile ? "small" : "medium"}
            />
          </div>
        </Box>
      )}

      {/* Results Table */}
      {hasSearched && !isLoading && (
        <Paper className={classes.resultsContainer}>
          {searchResults.length > 0 ? (
            <TableContainer className={classes.tableContainer}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className="dateColumn">Date</TableCell>
                    <TableCell className="userColumn">User</TableCell>
                    <TableCell className="clientColumn">Client</TableCell>
                    <TableCell className="hoursColumn">Hours</TableCell>
                    <TableCell className="descriptionColumn">Description</TableCell>
                    <TableCell className="platformColumn">Platform</TableCell>
                    <TableCell className="categoryColumn">Category</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((hour, index) => {
                    // Check if user can see this entry's hours (super admin or own entry)
                    const canSeeHours = user.type === 'super_admin' || hour.Who === user.username;
                    
                    return (
                      <TableRow key={index}>
                        <TableCell className="dateColumn">{moment(hour.Completed).format('MM/DD/YYYY')}</TableCell>
                        <TableCell className="userColumn">{hour.Who}</TableCell>
                        <TableCell className="clientColumn">{hour.ClientName || `ID: ${hour.ID}`}</TableCell>
                        <TableCell className="hoursColumn">
                          {canSeeHours ? hour.Qty : '-'}
                        </TableCell>
                        <TableCell className="descriptionColumn">
                        <div className="descriptionContent">
                          <div 
                            className={`descriptionText ${expandedRows.has(index) ? 'expanded' : 'collapsed'}`}
                            dangerouslySetInnerHTML={{ __html: hour.Description }} 
                          />
                          {isTextLongEnoughForReadMore(hour.Description) && (
                            <span 
                              className="readMoreLink"
                              onClick={() => toggleRowExpansion(index)}
                            >
                              {expandedRows.has(index) ? 'Show less' : 'Read more'}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="platformColumn">{hour.Platform || '-'}</TableCell>
                      <TableCell className="categoryColumn">
                        <div>
                          {hour.Category}
                          {hour.isRND === 1 && (
                            <Chip 
                              size="small" 
                              label="R&D" 
                              style={{ marginLeft: 4, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          {hour.DorI === 'Direct' ? (
                            <Chip 
                              size="small" 
                              label="Direct" 
                              color="primary"
                              style={{ height: 20, fontSize: '0.7rem' }}
                            />
                          ) : (
                            <Chip 
                              size="small" 
                              label="Indirect"
                              style={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <div className={classes.noResults}>
              <Typography variant="h6">No results found</Typography>
              <Typography color="textSecondary">
                Try adjusting your search criteria
              </Typography>
            </div>
          )}
        </Paper>
      )}
    </div>
  );
};

export default SearchHours;
